import { Pencil, Trash2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';
import type { ScheduledReportDto } from '@/features/reports/api/reports-api';
import { REPORT_TYPE_LABELS, FREQUENCY_LABELS } from './scheduled-report-labels';

interface ScheduledReportCardProps {
  schedule: ScheduledReportDto;
  canRunNow: boolean;
  isBusy: boolean;
  statusMessage?: string;
  statusMessageVariant?: 'success' | 'error';
  onEdit: (schedule: ScheduledReportDto) => void;
  onCancel: (schedule: ScheduledReportDto) => void;
  onRunNow: (schedule: ScheduledReportDto) => void;
}

// Mobile-first stacked layout. The table row variant in scheduled-report-row.tsx
// is the desktop counterpart; both pull from the same labels module so a
// label change ripples through.
export function ScheduledReportCard({
  schedule,
  canRunNow,
  isBusy,
  statusMessage,
  statusMessageVariant = 'success',
  onEdit,
  onCancel,
  onRunNow,
}: ScheduledReportCardProps) {
  const reportLabel = REPORT_TYPE_LABELS[schedule.reportType] ?? schedule.reportType;
  const frequencyLabel = FREQUENCY_LABELS[schedule.frequency] ?? schedule.frequency;

  return (
    <Card variant="elevated" padding="md">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {reportLabel}
          </span>
          <Badge variant="default" size="sm">{schedule.format.toUpperCase()}</Badge>
        </div>
        <Badge variant={schedule.isActive ? 'success' : 'secondary'} size="sm" dot>
          {schedule.isActive ? 'Active' : 'Paused'}
        </Badge>
      </div>
      <dl className="text-xs text-gray-600 dark:text-gray-400 space-y-1 mb-3">
        <div className="flex justify-between gap-2">
          <dt className="text-gray-400">Frequency</dt>
          <dd className="text-gray-800 dark:text-gray-200">{frequencyLabel}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-gray-400">Email</dt>
          <dd className="text-gray-800 dark:text-gray-200 break-all text-right">{schedule.deliveryEmail}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-gray-400">Subject</dt>
          {schedule.subject && schedule.subject.trim().length > 0 ? (
            <dd
              className="text-gray-800 dark:text-gray-200 break-words text-right"
              data-testid={`scheduled-report-subject-mobile-${schedule.id}`}
            >
              {schedule.subject}
            </dd>
          ) : (
            <dd
              className="italic text-gray-500 dark:text-gray-400 text-right"
              data-testid={`scheduled-report-subject-mobile-${schedule.id}`}
            >
              Using default
            </dd>
          )}
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-gray-400">Next run</dt>
          <dd className="text-gray-800 dark:text-gray-200">
            {schedule.nextRunAt ? formatDate(schedule.nextRunAt) : '—'}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-gray-400">Last run</dt>
          <dd className="text-gray-800 dark:text-gray-200">
            {schedule.lastRunAt ? formatDate(schedule.lastRunAt) : '—'}
          </dd>
        </div>
      </dl>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(schedule)}
          disabled={isBusy}
          data-testid={`scheduled-report-edit-mobile-${schedule.id}`}
        >
          <Pencil className="w-3.5 h-3.5" />
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onCancel(schedule)}
          disabled={isBusy}
          data-testid={`scheduled-report-cancel-mobile-${schedule.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
          Cancel
        </Button>
        {canRunNow && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onRunNow(schedule)}
            isLoading={isBusy}
            data-testid={`scheduled-report-run-mobile-${schedule.id}`}
          >
            {!isBusy && <Play className="w-3.5 h-3.5" />}
            Run now
          </Button>
        )}
      </div>
      {statusMessage && (
        <p
          className={
            statusMessageVariant === 'error'
              ? 'mt-2 text-xs text-error-600'
              : 'mt-2 text-xs text-success-600'
          }
          role="status"
        >
          {statusMessage}
        </p>
      )}
    </Card>
  );
}
